import { default as asyncify } from 'owa-apollo/lib/util/callbackToAsyncIterator';
import { isHxFallbackResult, HxFallbackResult } from 'owa-graph-hx-fallback-result';
import { trace } from 'owa-trace';

type CallbackWithPayload<T> = (payload: T) => void;

/**
 * Interface to hold subscribe and unsubscribe functions to be wrapped by asyncify for GQL subscription
 */
export interface SubscriptionFactory<Payload, Cleanup> {
    /**
     * Callback registration function that invokes the non-gql logic to subscribe to notifications to the server (OWA server or Hx etc).
     */
    subscribe: (callback: CallbackWithPayload<Payload>) => Promise<Cleanup>;
    /**
     * Unsubscribe function that takes in the callback function and invokes the non-gql logic to unsubscribe from the server notifications
     */
    unSubscribe: (args: Cleanup) => void;
}

/**
 * Converts the callback registration subscription function to GQL style AsyncIterator that wraps the future incoming stream of notifications
 * @param subscriptionFactoryFunction function that takes in arguments and returns a subscription resolver factory with subscribe and unsubscribe functions
 */
export function createSubscriptionResolver<Payload, Cleanup>(
    subscriptionFactoryFunction: (
        parent,
        args,
        context,
        info
    ) => Promise<SubscriptionFactory<Payload, Cleanup> | HxFallbackResult>
) {
    /**
     * Subscribe needs to return an AsyncIterator
     * wrapping the future stream of events. Asyncify
     * takes as input a callback registration function
     * and produces an AsyncIterator which registers a callback
     * and pushes an event in the AsyncIterator whenever the
     * callback is called.
     *
     * We pass an onClose function into asyncify which gets
     * called when the client unsubscribes. The onClose
     * function is called with whatever the callback registration
     * returned, so onClose has information aboout what to clean up.
     */
    const subscribe = async (parent, args, context, info) => {
        let result = await subscriptionFactoryFunction(parent, args, context, info);

        if (isHxFallbackResult(result)) {
            return result;
        } else {
            let subscriptionFactory = result;
            return asyncify(subscriptionFactory.subscribe, {
                onClose: subscriptionFactory.unSubscribe,
                onError: onSubscriptionError,
            });
        }
    };

    /**
     * Resolve is called for each event to allow the resolver
     * to alter the payload before it is sent to the client.
     */
    const resolve = payload => {
        return payload;
    };

    return {
        subscribe,
        resolve,
    };
}

function onSubscriptionError(error: Error) {
    trace.warn(`Subscription error: ${error.message}`);
}
